import { dropTimeZoneFromDate } from './../../shared/libraries/utils/date'
import { getNumberOfHouseholdsAndDensityForBoundaries } from '../Map'
import { isPathFinderSupportsCountry } from '../../shared/libraries/geo'
import moment from 'moment'

/**
 * Generates string from metrics name and value
 *
 * @typedef FormatMetricStringForTablesArgsObj
 * @property {String} name -  Name of the metric
 * @property {Number} value - Number of the conversions for this metric
 *
 * @param {FormatMetricStringForTablesArgsObj}
 *
 * @return {String} - Formatted string or display in the tables (Signups: 5)
 */
const formatMetricStringForTables = ({ name, value }) =>
  name && value ? `${name}: ${value}` : ''

/**
 * Finds primary and secondaru metrics based on their ids
 * Primary metric is the metric with a bigger ID
 * Based on Bilal's comment:
 * "Metric 1 is the campaign's primary metric. Metric 2 is the first metric (minimum database ID) that is NOT metric 1."
 *
 * @param {Array} metric1 - First element is a metric ID second is object that contains metrics name and value
 * @param {Array} metric2 - First element is a metric ID second is object that contains metrics name and value
 *
 * @return {Object} - Contains primaryMetricValue and secondaryMetricValue
 */
const findPrimaryAndSecondaryMetrics = (metric1, metric2) => {
  let primaryMetricValue = {}
  let secondaryMetricValue = {}
  if (metric1 && metric2) {
    primaryMetricValue = metric1[0] > metric2[0] ? metric1[1] : metric2[1]
    secondaryMetricValue = metric1[0] < metric2[0] ? metric1[1] : metric2[1]
  } else if (metric1) {
    primaryMetricValue = metric1[1]
  }
  return {
    primaryMetricValue,
    secondaryMetricValue,
  }
}
/**
 * Proceeds conversions, calculates unique metrics and generate their values
 *
 * @param {Object} conversions - Array of Conversions and Metrics
 *
 * @typedef GetMetricResultsFromConversionsMap
 * @key - Represents a name of the metric
 * @value - Represents a number of the comversions for this metric
 *
 * @return {GetMetricResultsFromConversionsMap}
 *
 * @example
 * const metricResultsMap = getMetricResultsFromConversionsMap(conversions)
 */
export const getMetricResultsFromConversionsMap = conversions => {
  if (conversions.length > 0) {
    const metricValuesMap = conversions.reduce((acc, conversion) => {
      const { id, name } =
        conversion.metric /*NOTE: Not sure why this is now needed */ ||
        conversion.Metric
      const previousValue = acc.get(id)
      if (!previousValue) {
        acc.set(id, { name, value: 1 })
      }
      const value = previousValue ? previousValue.value + 1 : 1
      acc.set(id, { name, value })
      return acc
    }, new Map())
    return metricValuesMap
  }
  return new Map()
}

/**
 * Calls getMetricResultsFromConversionsMap and then proceeds results
 * into object that contains formatted strings for display in the tables
 *
 * @param {Object} conversions - Array of Conversions and Metrics
 *
 * @typedef GetMetricResultsFromConversionsReturnObj
 * @property {String} firstMetric - String that represents first metric's name and value
 * @property {String} secondMetric - String that represents second metric's name and value
 *
 * @return {GetMetricResultsFromConversionsReturnObj}
 *
 * @example
 * const metricResults = getMetricResultsFromConversions(conversions)
 */
export const getMetricResultsFromConversions = conversions => {
  let metrics = {
    primaryMetric: '',
    secondaryMetric: '',
  }
  if (conversions.length > 0) {
    const res = getMetricResultsFromConversionsMap(conversions)
    const iterator = res.entries()
    const metric1 = iterator.next().value
    const metric2 = iterator.next().value

    const {
      primaryMetricValue,
      secondaryMetricValue,
    } = findPrimaryAndSecondaryMetrics(metric1, metric2)

    metrics = {
      primaryMetric: primaryMetricValue
        ? formatMetricStringForTables({
            name: primaryMetricValue.name,
            value: primaryMetricValue.value,
          })
        : '',
      secondaryMetric: secondaryMetricValue
        ? formatMetricStringForTables({
            name: secondaryMetricValue.name,
            value: secondaryMetricValue.value,
          })
        : '',
    }
  }
  return metrics
}

/**
 * Does the computing required to figure out if it's a morning, lunch or
 * afternoon mission.
 * @param {string} dateTime The mission date time as a string with the time
 * zone offset.
 * @param {string} lunchCheckInTime The time of day it becomes a lunch mission
 * e.g. 11:00:00
 * @param {string} afternoonCheckInTime The time of day it becomes an afternoon
 * mission.
 * @returns {string} Returns either MORNING, AFTERNOON or LUNCH
 */
export const calculateTimeOfDay = (
  dateTime,
  lunchCheckInTime,
  afternoonCheckInTime,
) => {
  var localDateTimeStr = dropTimeZoneFromDate(dateTime)
  var localTime = localDateTimeStr.slice('yyyy-mm-ddT'.length)
  if (localTime < lunchCheckInTime) return 'MORNING'
  if (localTime < afternoonCheckInTime) return 'LUNCH'
  return 'AFTERNOON'
}

/**
 * Checks if it is possible to get households for the country and if yes
 * returns households and density for the mission's area
 *
 * @param {Array<Objects>} param.missions
 * @param {String} param.countryCode
 */
export const getNumberOfHouseholdsAndDensityForMissions = async ({
  missions,
  countryCode,
}) => {
  if (!isPathFinderSupportsCountry(countryCode)) return {}

  if (missions.length <= 0) return {}

  const boundaries = missions.map(({ boundary }) => boundary)
  const householdsAndDensity = await getNumberOfHouseholdsAndDensityForBoundaries(
    {
      boundaries,
      countryCode,
    },
  )

  return missions.reduce((acc, { id, boundary }, index) => {
    acc[id] = householdsAndDensity[index]
    return acc
  }, {})
}

/**
 * Returns a hashmap of all the missions grouped into their respective year
 * and week. The key will be `yyyy-w`, for example `2019-24` or `2019-4`.
 * The value will be an array of missions
 * @param {Array<Mission>}
 * @returns {Object<string, Array<Mission>>}
 */
export const groupMissionsByWeek = function(missions) {
  var groupedMissions = {}
  // Shuffle the city plan missions into weeks/rosters

  missions.forEach(mission => {
    var localDateTime = dropTimeZoneFromDate(mission.dateTime)

    const year = new Date(localDateTime).getFullYear()
    const weekNum = moment(localDateTime).isoWeek()

    groupedMissions['' + year + '-' + weekNum] =
      groupedMissions['' + year + '-' + weekNum] || []
    groupedMissions['' + year + '-' + weekNum].push(mission)
  })
  // Allow forcasting this week and 2 weeks into the future
  const testDate = new Date()
  let i = 2
  while (i >= 0) {
    const year = new Date(testDate).getFullYear()
    const weekNum = moment(testDate).isoWeek()

    groupedMissions['' + year + '-' + weekNum] =
      groupedMissions['' + year + '-' + weekNum] || []
    testDate.setDate(testDate.getDate() + 7)
    i--
  }
  return groupedMissions
}

/**
 * @typedef MockedRoster
 * @property {number} weekNum
 * @property {number} year
 * @property {Array<Mission>} missions
 *
 * Mocks rosters from an array of missions. Groups missions into their
 * respective weeks and returns them in sorted date order earliest to latest
 * @param {Array<Mission>} missions
 * @return {Array<MockedRoster>}
 */
export const mockRosters = function(missions) {
  var groupedMissions = groupMissionsByWeek(missions)
  return Object.keys(groupedMissions)
    .map(weekNumYear => {
      return {
        weekNum: weekNumYear.split('-')[1],
        year: weekNumYear.split('-')[0],
        missions: groupedMissions[weekNumYear],
      }
    })
    .sort((a, b) => {
      var aYear = Number(a.year)
      var bYear = Number(b.year)
      var aWeek = Number(a.weekNum)
      var bWeek = Number(b.weekNum)
      if (aYear < bYear) return -1
      if (aYear > bYear) return 1
      if (aWeek < bWeek) return -1
      if (aWeek > bWeek) return 1
      return 0
    })
}



// WEBPACK FOOTER //
// ./src/Utils/Mission/index.js